import type { Ball, Vec2 } from './ball.js'
import { BALL_UPRIGHT_RESTITUTION, BALL_BALL_RESTITUTION } from './constants.js'

export interface StaticCircle {
  pos: Vec2
  radius: number
}

/**
 * Detect and resolve a collision between a moving ball and a static circle.
 *
 * Physics:
 *   - Detection: overlap when distance < ball.radius + circle.radius
 *   - Separation: push ball out along collision normal (prevent tunnelling)
 *   - Velocity response: reflect component along normal, scaled by restitution
 *
 * The tangential component is preserved (no friction on upright surface for v1).
 *
 * @param ball        Moving ball (mutated)
 * @param circle      Static circular obstacle (e.g. hoop upright)
 * @param restitution Coefficient of restitution (default: BALL_UPRIGHT_RESTITUTION)
 * @returns true if a collision occurred
 */
export function resolveCircleCollision(
  ball: Ball,
  circle: StaticCircle,
  restitution = BALL_UPRIGHT_RESTITUTION
): boolean {
  const dx = ball.pos.x - circle.pos.x
  const dy = ball.pos.y - circle.pos.y
  const distSq = dx * dx + dy * dy
  const minDist = ball.radius + circle.radius

  if (distSq >= minDist * minDist) return false

  const dist = Math.sqrt(distSq)

  // Collision normal (from circle centre toward ball centre)
  const nx = dist > 1e-9 ? dx / dist : 1
  const ny = dist > 1e-9 ? dy / dist : 0

  // Separate: push ball out of overlap
  const overlap = minDist - dist
  ball.pos.x += nx * overlap
  ball.pos.y += ny * overlap

  // Velocity along normal (approaching component)
  const vn = ball.vel.x * nx + ball.vel.y * ny

  // Only resolve if ball is moving toward the circle
  if (vn >= 0) return true

  // Reflect normal component with restitution; tangential component unchanged
  const impulse = -(1 + restitution) * vn
  ball.vel.x += impulse * nx
  ball.vel.y += impulse * ny

  return true
}

/**
 * Resolve a collision between two moving balls (equal mass).
 * Mutates both balls: separates them and exchanges velocity along the collision normal.
 */
export function resolveBallBallCollision(
  a: Ball,
  b: Ball,
  restitution = BALL_BALL_RESTITUTION
): boolean {
  const dx = b.pos.x - a.pos.x
  const dy = b.pos.y - a.pos.y
  const distSq = dx * dx + dy * dy
  const minDist = a.radius + b.radius

  if (distSq >= minDist * minDist) return false

  const dist = Math.sqrt(distSq)
  const nx = dist > 1e-9 ? dx / dist : 1
  const ny = dist > 1e-9 ? dy / dist : 0

  // Push balls apart equally
  const overlap = (minDist - dist) * 0.5
  a.pos.x -= nx * overlap
  a.pos.y -= ny * overlap
  b.pos.x += nx * overlap
  b.pos.y += ny * overlap

  // Relative velocity of a w.r.t. b along collision normal
  const vRelN = (a.vel.x - b.vel.x) * nx + (a.vel.y - b.vel.y) * ny
  if (vRelN <= 0) return true  // already separating

  // Impulse (equal masses: divide by 2)
  const j = -(1 + restitution) / 2 * vRelN
  a.vel.x += j * nx
  a.vel.y += j * ny
  b.vel.x -= j * nx
  b.vel.y -= j * ny

  if (a.speed > 0) a.state = 'rolling'
  b.state = 'rolling'

  return true
}

/**
 * Distance between two 2D points.
 */
export function dist2(a: Vec2, b: Vec2): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2)
}
